import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { TextLink, Strong, IconCaution, IconInfo, IconCritical, IconPromote, IconPositive, Box, PageBlock, Text, Alert, TextLinkButton, Dialog } from "braid-design-system";
import { useState, useEffect } from "react";
import { RichText } from "@seek/cmsu-rich-text";
import { Project, initHyGraphClient } from "@seek/cmsu-cms-connect";
import { gql } from "graphql-request";
const HygraphRichText = ({ richTextData }) => /* @__PURE__ */ jsx(
  RichText,
  {
    content: richTextData == null ? void 0 : richTextData.raw,
    references: richTextData == null ? void 0 : richTextData.references,
    renderers: {
      a: ({ children, href }) => /* @__PURE__ */ jsx(TextLink, { target: "_blank", href: href || "", children }),
      embed: {
        EmbedSingleLineText: ({ value, bold }) => bold ? /* @__PURE__ */ jsx(Strong, { children: value }) : /* @__PURE__ */ jsx("span", { children: value })
      },
      link: {
        EmbedSingleLineText: ({ value, children }) => /* @__PURE__ */ jsx(TextLink, { target: "_blank", href: value, children })
      },
      p: ({ children }) => /* @__PURE__ */ jsx("span", { children })
    }
  }
);
const bannerStyle = {
  caution: {
    background: "cautionLight",
    icon: /* @__PURE__ */ jsx(IconCaution, {})
  },
  info: {
    background: "infoLight",
    icon: /* @__PURE__ */ jsx(IconInfo, {})
  },
  critical: {
    background: "criticalLight",
    icon: /* @__PURE__ */ jsx(IconCritical, {})
  },
  promote: {
    background: "promoteLight",
    icon: /* @__PURE__ */ jsx(IconPromote, {})
  },
  positive: {
    background: "positiveLight",
    icon: /* @__PURE__ */ jsx(IconPositive, {})
  }
};
const environments = {
  development: Project.ApacDev,
  staging: Project.ApacStg,
  production: Project.Apac
};
const getClient = (environment) => {
  const client = initHyGraphClient(environments[environment]);
  return client;
};
const BannersQuery = gql`
  query GetPlatformBanners(
    $platform: [ManagedContentBannerPlatform!]
    $context: [ManagedContentBannerContext!]
    $locale: [Locale!]!
  ) {
    platformBanners: managedContentBanners(
      locales: $locale
      where: { platform_contains_all: $platform, context: [] }
    ) {
      context
      tone
      display
      bannerStart
      bannerEnd
      message {
        raw
        references {
          ... on EmbedSingleLineText {
            id
            value
            bold
          }
        }
      }
      moreInfoContent {
        raw
        references {
          ... on EmbedSingleLineText {
            id
            value
            bold
          }
        }
      }
      moreInfoText
    }
    contextBanners: managedContentBanners(
      locales: $locale
      where: {
        platform_contains_all: $platform
        context_contains_all: $context
      }
    ) {
      context
      tone
      display
      bannerStart
      bannerEnd
      message {
        raw
        references {
          ... on EmbedSingleLineText {
            id
            value
            bold
          }
        }
      }
      moreInfoContent {
        raw
        references {
          ... on EmbedSingleLineText {
            id
            value
            bold
          }
        }
      }
      moreInfoText
    }
  }
`;
const getBannerContent = async (locale, platform, environment, context) => {
  const graphqlClient = getClient(environment);
  const formatLocale = locale.replace("-", "_");
  const result = await graphqlClient.request(BannersQuery, {
    platform: [platform],
    context: context ? [context] : [],
    locale: [formatLocale]
  });
  if (!context) return result.platformBanners;
  if (result.contextBanners.length > 0) return result.contextBanners;
  return result.platformBanners;
};
const getLocalStorageItem = (item) => {
  var _a;
  return typeof window !== "undefined" ? (_a = window.localStorage) == null ? void 0 : _a.getItem(item) : void 0;
};
const SIMULATED_DATE_STORAGE_KEY = "seek:simulatedDate";
const getSimulatedDate = () => {
  const storageDateString = getLocalStorageItem(SIMULATED_DATE_STORAGE_KEY);
  if (!storageDateString || isNaN(Date.parse(storageDateString))) {
    return void 0;
  }
  return new Date(storageDateString);
};
const isBannerInDateRange = (banner, currentDate) => {
  const dateRange = banner.bannerEnd && banner.bannerStart && {
    start: new Date(banner.bannerStart),
    end: new Date(banner.bannerEnd)
  };
  if (!dateRange) {
    return true;
  }
  const compareDate = currentDate || /* @__PURE__ */ new Date();
  return compareDate >= dateRange.start && compareDate <= dateRange.end;
};
const bannerOrder = [
  "critical",
  "caution",
  "info",
  "promote",
  "positive",
  "neutral"
];
const sortBannersByTone = (banners) => banners.sort(
  (a, b) => bannerOrder.indexOf(a.tone) - bannerOrder.indexOf(b.tone)
);
const ManagedContentBanner = ({
  platform,
  locale = "en-AU",
  environment,
  context,
  style = "banner"
}) => {
  const [banners, setBanners] = useState();
  const [loading, setLoading] = useState(true);
  const [showMoreInfo, setShowMoreInfo] = useState(false);
  if (!platform) {
    throw new Error("platform is not defined");
  }
  if (!environment) {
    throw new Error("environment is not defined");
  }
  const catchHygraphError = (error) => {
    console.error(error);
  };
  useEffect(() => {
    const fetchHygraphData = async () => {
      try {
        const hygraphData = await getBannerContent(
          locale,
          platform,
          environment,
          context
        );
        setBanners(sortBannersByTone(hygraphData));
        setLoading(false);
      } catch (error) {
        catchHygraphError(error);
      }
    };
    fetchHygraphData();
  }, [platform, locale, environment, context]);
  const simulatedDate = getSimulatedDate();
  const selectedBanner = banners == null ? void 0 : banners.find(
    (banner2) => banner2.display && isBannerInDateRange(banner2, simulatedDate)
  );
  if (loading || !selectedBanner) return null;
  const banner = bannerStyle[selectedBanner.tone];
  if (!banner) return null;
  function BannerContent() {
    if (loading || !selectedBanner) return null;
    const hasMoreInfo = selectedBanner.moreInfoText && selectedBanner.moreInfoContent;
    const spacer = /* @__PURE__ */ jsx(Fragment, { children: " " });
    return /* @__PURE__ */ jsxs(Box, { display: "inline", children: [
      /* @__PURE__ */ jsx(HygraphRichText, { richTextData: selectedBanner.message }),
      hasMoreInfo ? /* @__PURE__ */ jsxs(Fragment, { children: [
        spacer,
        /* @__PURE__ */ jsx(
          TextLinkButton,
          {
            hitArea: "large",
            onClick: () => setShowMoreInfo(true),
            children: selectedBanner.moreInfoText
          }
        ),
        /* @__PURE__ */ jsx(
          Dialog,
          {
            id: "banner",
            title: "We're improving SEEK",
            open: showMoreInfo,
            onClose: () => setShowMoreInfo(false),
            children: /* @__PURE__ */ jsx(Text, { children: /* @__PURE__ */ jsx(
              HygraphRichText,
              {
                richTextData: selectedBanner.moreInfoContent
              }
            ) })
          }
        )
      ] }) : null
    ] });
  }
  if (style === "banner")
    return /* @__PURE__ */ jsx(Box, { background: banner.background, paddingY: "medium", children: /* @__PURE__ */ jsx(PageBlock, { width: "medium", children: /* @__PURE__ */ jsx(Text, { icon: banner.icon, children: /* @__PURE__ */ jsx(BannerContent, {}) }) }) });
  if (style === "alert")
    return /* @__PURE__ */ jsx(Alert, { tone: selectedBanner.tone, children: /* @__PURE__ */ jsx(Text, { children: /* @__PURE__ */ jsx(BannerContent, {}) }) });
  return null;
};
ManagedContentBanner.displayName = "ManagedContentBanner";
export {
  ManagedContentBanner
};
